import { getGridNavigationTransition } from '@/lib/assets/grid-navigation-transition'
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react'
import {
  GRID_PERFORMANCE_EVENT,
  finishGridMeasurement,
  getGridMeasurement,
  markGridDataReady,
  navigationPath,
} from '@/lib/analytics/grid-navigation-performance'

export default function useGridNavigationPerformance({
  enabled,
  isLoading,
  hasAssets,
  scope,
  contentVersion,
  serverDataMs,
  serverPrepareMs,
}: {
  enabled: boolean
  isLoading: boolean
  hasAssets: boolean
  scope: RefObject<HTMLElement | null>
  contentVersion?: unknown
  serverDataMs?: number
  serverPrepareMs?: number
}) {
  const [id, setId] = useState(() => getGridMeasurement()?.id)
  const versionAtStart = useRef(contentVersion)
  const sawLoading = useRef(false)
  useEffect(() => {
    if (!enabled) return
    const onStart = () => {
      versionAtStart.current = contentVersion
      sawLoading.current = false
      setId(getGridMeasurement()?.id)
    }
    window.addEventListener(GRID_PERFORMANCE_EVENT, onStart)
    return () => window.removeEventListener(GRID_PERFORMANCE_EVENT, onStart)
  }, [contentVersion, enabled])

  useLayoutEffect(() => {
    if (!enabled) return
    const active = getGridMeasurement()
    if (!active || active.id !== id) return
    if (isLoading) {
      sawLoading.current = true
      return
    }
    if (active.destination) {
      if (navigationPath(window.location.href) !== active.destination) return
    } else if (!sawLoading.current && versionAtStart.current === contentVersion) return
    if (!scope.current || contentVersion === undefined) return
    markGridDataReady(active.id, serverDataMs, serverPrepareMs)
    let frame = 0
    let cancelled = false
    const imagesDeadline = performance.now() + 1000
    let lastCheck = -Infinity
    const observeGrid = () => {
      if (cancelled || getGridMeasurement()?.id !== active.id) return
      if (getGridNavigationTransition() || performance.now() - lastCheck < 100) {
        frame = requestAnimationFrame(observeGrid)
        return
      }
      lastCheck = performance.now()
      const root = scope.current
      const cards = root ? Array.from(root.querySelectorAll<HTMLElement>('[data-grid-asset-key]')) : []
      const visible = cards
        .filter((card) => {
          const bounds = card.getBoundingClientRect()
          return (
            bounds.width > 0 &&
            bounds.height > 0 &&
            bounds.bottom > 0 &&
            bounds.top < window.innerHeight &&
            getComputedStyle(card).visibility !== 'hidden' &&
            getComputedStyle(card).opacity !== '0'
          )
        })
        .slice(0, 48)
      const imagesReady = visible.every((card) => {
        const image = card.querySelector<HTMLImageElement>('img')
        return !image || (image.complete && image.naturalWidth > 0)
      })
      if (hasAssets && (!visible.length || (!imagesReady && performance.now() < imagesDeadline))) {
        frame = requestAnimationFrame(observeGrid)
        return
      }
      frame = requestAnimationFrame(() => {
        if (!cancelled) finishGridMeasurement(active.id, imagesReady)
      })
    }
    frame = requestAnimationFrame(observeGrid)
    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
    }
  }, [contentVersion, enabled, hasAssets, id, isLoading, scope, serverDataMs, serverPrepareMs])
}
